import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import "./AlumniRegistration.css";

const PROFILE_CACHE_KEY = "alumniProfileCache";

const safeParse = (value, fallback = {}) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const saveProfileCache = (profile) => {
  if (!profile || !profile.email) return;
  const emailKey = String(profile.email).toLowerCase();
  const cache = safeParse(localStorage.getItem(PROFILE_CACHE_KEY), {});
  cache[emailKey] = profile;
  localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(cache));
};

const cleanText = (value) => (typeof value === "string" ? value.trim() : value);

/* ================= CUSTOM SVG ICONS ================= */
const HdfcBankIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18" />
    <path d="M3 10h18" />
    <path d="M5 6l7-3 7 3" />
    <path d="M4 10v11" />
    <path d="M20 10v11" />
    <path d="M8 14v3" />
    <path d="M12 14v3" />
    <path d="M16 14v3" />
  </svg>
);

const SecureShieldIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

/* ================= IMAGE COMPRESSION ENGINE ================= */
const processImage = (file) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) return reject(new Error("Please select a valid image file"));
    if (file.size > 2 * 1024 * 1024) return reject(new Error("Image size must be less than 2MB"));

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const maxWidth = 800;
        const maxHeight = 800;

        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = (height / width) * maxWidth;
            width = maxWidth;
          } else {
            width = (width / height) * maxHeight;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        const compressed = canvas.toDataURL("image/jpeg", 0.8);
        resolve(compressed);
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = e.target.result;
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

/* ================= COMPONENT ================= */
const AlumniRegistration = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    profilePhoto: "",
    gender: "",
    dob: "",
    email: "",
    mobile: "",
    address: "",
    city: "",
    country: "",
    bio: "",
    course: "",
    degree: "",
    batchYear: "",
    studentId: "", 
    college: "CIT Bangalore",
    isWorking: false,
    companyName: "",
    jobTitle: "",
    industry: "",
    workExperience: "",
    workLocation: "",
    linkedin: "",
    workMobile: "",
    transactionId: "",
    password: "",
    confirmPassword: ""
  });

  const [showPopup, setShowPopup] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const genders = ["Male", "Female", "Other"];

  const handleChange = async (e) => {
    const { name, value, files } = e.target;

    if (name === "profilePhoto" && files && files[0]) {
      try {
        const compressed = await processImage(files[0]);
        setFormData((prev) => ({ ...prev, profilePhoto: compressed }));
        if (errors.profilePhoto) setErrors((prev) => ({ ...prev, profilePhoto: null }));
      } catch (err) {
        setErrors((prev) => ({ ...prev, profilePhoto: err.message || "Invalid image file" }));
      }
      return;
    }

    if (name === "mobile" || name === "workMobile") {
      const onlyDigits = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: onlyDigits }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleStatusChange = (e) => {
    const isWorking = e.target.value === "working";
    setFormData((prev) => ({
      ...prev,
      isWorking,
      companyName: isWorking ? prev.companyName : "",
      jobTitle: isWorking ? prev.jobTitle : "",
      industry: isWorking ? prev.industry : "",
      workExperience: isWorking ? prev.workExperience : "",
      workLocation: isWorking ? prev.workLocation : "",
      linkedin: isWorking ? prev.linkedin : "",
      workMobile: isWorking ? prev.workMobile : ""
    }));

    setErrors((prev) => ({
      ...prev,
      companyName: null,
      jobTitle: null,
      workLocation: null,
      workMobile: null
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.email || !/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = "Valid email is required";
    if (!formData.mobile || !/^\d{10}$/.test(formData.mobile)) newErrors.mobile = "Valid 10-digit phone number is required";
    if (!formData.course.trim()) newErrors.course = "Department / course is required";
    if (!formData.degree.trim()) newErrors.degree = "Degree is required";
    if (!formData.batchYear.trim()) newErrors.batchYear = "Batch / graduation year is required";
    if (!formData.college.trim()) newErrors.college = "College / university name is required";
    if (!formData.password || formData.password.length < 6) newErrors.password = "Password must be at least 6 characters";
    if (!formData.confirmPassword) newErrors.confirmPassword = "Confirm password is required";
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords do not match";

    if (formData.isWorking) {
      if (!formData.companyName.trim()) newErrors.companyName = "Current company is required";
      if (!formData.jobTitle.trim()) newErrors.jobTitle = "Job title is required";
      if (!formData.workLocation.trim()) newErrors.workLocation = "Work location is required";
      if (formData.workMobile && !/^\d{10}$/.test(formData.workMobile)) newErrors.workMobile = "Valid 10-digit work phone is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const emailLower = cleanText(formData.email).toLowerCase();
      const regNo = (formData.studentId || "").trim();

      const payload = {
        name: cleanText(formData.name),
        ...(formData.profilePhoto ? { profilePhoto: formData.profilePhoto } : {}),
        gender: cleanText(formData.gender) || "",
        dob: formData.dob || "",
        email: emailLower,
        mobile: cleanText(formData.mobile),
        address: cleanText(formData.address) || "",
        city: cleanText(formData.city) || "",
        country: cleanText(formData.country) || "",
        bio: cleanText(formData.bio) || "",
        course: cleanText(formData.course),
        degree: cleanText(formData.degree),
        batchYear: cleanText(formData.batchYear),
        ...(regNo ? { alumniId: regNo } : {}),
        college: cleanText(formData.college),
        isWorking: !!formData.isWorking,
        companyName: cleanText(formData.companyName) || "",
        jobTitle: cleanText(formData.jobTitle) || "",
        industry: cleanText(formData.industry) || "",
        workExperience: cleanText(formData.workExperience) || "",
        workLocation: cleanText(formData.workLocation) || "",
        linkedin: cleanText(formData.linkedin) || "",
        workMobile: cleanText(formData.workMobile) || "",
        transactionId: cleanText(formData.transactionId) || "",
        password: formData.password
      };

      const res = await api.post("/api/auth/register", payload);

      const userData = res.data?.user || {};

      const userForStorage = {
        id: userData.id || "",
        _id: userData.id || "",
        name: userData.name || payload.name,
        email: userData.email || payload.email,
        status: userData.status || "Under Verification",
        alumniId: regNo || userData.alumniId || ""
      };

      localStorage.setItem("token", res.data.token);
      localStorage.setItem("loggedInUser", JSON.stringify(userForStorage));
      localStorage.setItem("lastRegisteredEmail", userForStorage.email);
      saveProfileCache(userForStorage);

      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        navigate("/login");
      }, 2200);
    } catch (error) {
      console.error("Registration Error:", error);

      const msg =
        error.response?.data?.message ||
        (!error.response ? "Cannot connect to server. Please ensure the backend is running." : "Registration failed.");

      if (msg.toLowerCase().includes("alumniid")) {
        setErrors({ studentId: msg });
      } else if (msg.toLowerCase().includes("email")) {
        setErrors({ email: msg });
      } else {
        setErrors({ form: msg });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="registration-page">
      <div className="registration-shell">
        
        {/* ================= STICKY SIDE HERO ================= */}
        <div className="registration-hero">
          <div className="registration-badge">Alumni Network Registration</div>
          <h1>Build Your Professional Alumni Profile</h1>
          <p>Create a complete alumni account with personal, academic, professional, and verification details.</p>
          
          <div className="registration-highlights">
            <div className="highlight-card">
              <h4>Institutional Identity</h4>
              <p>Connect with a worldwide verified network of successful batch legacy graduates.</p>
            </div>
            <div className="highlight-card">
              <h4>Direct Engagement</h4>
              <p>Gain access to restricted professional directories, placement boards, and mentorship drives.</p>
            </div>
          </div>
        </div>

        {/* ================= MAIN FORM CARD ================= */}
        <div className="registration-card">
          <div className="registration-header">
            <h2>CIT BLR Chapter - Alumni</h2>
            <p>Please complete all important details to create your alumni account.</p>
          </div>

          <form onSubmit={handleSubmit} className="registration-form">
            
            {/* 1. PROFILE */}
            <fieldset className="form-section">
              <legend>1️⃣ Profile Information</legend>

              <div className="grid-two">
                <div className="input-wrapper">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <span className="error-msg">{errors.name}</span>}
                </div>

                <div className="input-wrapper">
                  <label htmlFor="profilePhoto">Profile Photo</label>
                  <input
                    type="file"
                    id="profilePhoto"
                    name="profilePhoto"
                    accept="image/*"
                    onChange={handleChange}
                  />
                  {errors.profilePhoto && <span className="error-msg">{errors.profilePhoto}</span>}
                </div>
              </div>

              <div className="grid-two">
                <div className="input-wrapper">
                  <label htmlFor="gender">Gender</label>
                  <select id="gender" name="gender" value={formData.gender} onChange={handleChange}>
                    <option value="">Select gender</option>
                    {genders.map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>

                <div className="input-wrapper">
                  <label htmlFor="dob">Date of Birth</label>
                  <input type="date" id="dob" name="dob" value={formData.dob} onChange={handleChange} />
                </div>
              </div>

              <div className="grid-two">
                <div className="input-wrapper">
                  <label htmlFor="email">Email ID *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@email.com"
                  />
                  {errors.email && <span className="error-msg">{errors.email}</span>}
                </div>

                <div className="input-wrapper">
                  <label htmlFor="mobile">Phone Number *</label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    maxLength="10"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter 10-digit mobile number"
                  />
                  {errors.mobile && <span className="error-msg">{errors.mobile}</span>}
                </div>
              </div>
            </fieldset>

            {/* 2. EDUCATION */}
            <fieldset className="form-section">
              <legend>2️⃣ Education Details *</legend>

              <div className="input-wrapper">
                <label htmlFor="college">College / University Name</label>
                <input type="text" id="college" name="college" value={formData.college} readOnly />
                {errors.college && <span className="error-msg">{errors.college}</span>}
              </div>

              <div className="input-wrapper">
                <label htmlFor="batchYear">Batch / Graduation Year *</label>
                <input
                  type="text"
                  id="batchYear"
                  name="batchYear"
                  value={formData.batchYear}
                  onChange={handleChange}
                  placeholder="e.g. 2020"
                />
                {errors.batchYear && <span className="error-msg">{errors.batchYear}</span>}
              </div>

              <div className="input-wrapper">
                <label htmlFor="studentId">Registration Number</label>
                <input
                  type="text"
                  id="studentId"
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleChange}
                  placeholder="Enter registration number"
                />
                {errors.studentId && <span className="error-msg">{errors.studentId}</span>}
              </div>

              <div className="grid-two">
                <div className="input-wrapper">
                  <label htmlFor="degree">Degree *</label>
                  <input
                    type="text"
                    id="degree"
                    name="degree"
                    value={formData.degree}
                    onChange={handleChange}
                    placeholder="B.E, B.Tech, MBA, etc."
                  />
                  {errors.degree && <span className="error-msg">{errors.degree}</span>}
                </div>

                <div className="input-wrapper">
                  <label htmlFor="course">Department / Course *</label>
                  <input
                    type="text"
                    id="course"
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    placeholder="e.g. Computer Science"
                  />
                  {errors.course && <span className="error-msg">{errors.course}</span>}
                </div>
              </div>
            </fieldset>

            {/* 3. PROFESSIONAL */}
            <fieldset className="form-section">
              <legend>3️⃣ Professional Details</legend>

              <div className="status-switcher">
                <label className={`status-pill ${!formData.isWorking ? "active" : ""}`}>
                  <input type="radio" name="status" value="not-working" checked={!formData.isWorking} onChange={handleStatusChange} />
                  <span>Currently Not Working</span>
                </label>

                <label className={`status-pill ${formData.isWorking ? "active" : ""}`}>
                  <input type="radio" name="status" value="working" checked={formData.isWorking} onChange={handleStatusChange} />
                  <span>Currently Working</span>
                </label>
              </div>

              <div className="grid-two">
                <div className="input-wrapper">
                  <label htmlFor="companyName">Current Company {formData.isWorking && "*"}</label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Enter current company"
                  />
                  {errors.companyName && <span className="error-msg">{errors.companyName}</span>}
                </div>

                <div className="input-wrapper">
                  <label htmlFor="jobTitle">Job Title / Position {formData.isWorking && "*"}</label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    placeholder="Enter your job title"
                  />
                  {errors.jobTitle && <span className="error-msg">{errors.jobTitle}</span>}
                </div>
              </div>

              <div className="grid-two">
                <div className="input-wrapper">
                  <label htmlFor="workLocation">Work Location {formData.isWorking && "*"}</label>
                  <input
                    type="text"
                    id="workLocation"
                    name="workLocation"
                    value={formData.workLocation}
                    onChange={handleChange}
                    placeholder="City / Country"
                  />
                  {errors.workLocation && <span className="error-msg">{errors.workLocation}</span>}
                </div>

                <div className="input-wrapper">
                  <label htmlFor="linkedin">LinkedIn Profile</label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    value={formData.linkedin}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/your-profile"
                  />
                </div>
              </div>

              <div className="input-wrapper">
                <label htmlFor="workMobile">Official Contact Number</label>
                <input
                  type="tel"
                  id="workMobile"
                  name="workMobile"
                  maxLength="10"
                  value={formData.workMobile}
                  onChange={handleChange}
                  placeholder="Enter official contact number"
                />
                {errors.workMobile && <span className="error-msg">{errors.workMobile}</span>}
              </div>
            </fieldset>

            {/* 4. ACCOUNT SETTINGS */}
            <fieldset className="form-section">
              <legend>4️⃣ Account Settings *</legend>

              <div className="grid-two">
                <div className="input-wrapper">
                  <label htmlFor="password">Password *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Minimum 6 characters"
                  />
                  {errors.password && <span className="error-msg">{errors.password}</span>}
                </div>

                <div className="input-wrapper">
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter password"
                  />
                  {errors.confirmPassword && <span className="error-msg">{errors.confirmPassword}</span>}
                </div>
              </div>
            </fieldset>

            {/* ================= 5. INTEGRATED BANKING TRANSFER / QR VERIFICATION ================= */}
            <fieldset className="form-section premium-banking-section">
              <legend className="banking-legend">
                <SecureShieldIcon />
                <span>5️⃣ Member registration fee</span>
              </legend>

              <p className="banking-desc-text">
                Support the enduring legacy of our institutional initiatives. Scan the secure HDFC Bank UPI QR code below via any UPI application (GPay, PhonePe, Paytm, BHIM) to make a direct transfer.
              </p>

              <div className="banking-dual-layout">
                
                {/* Left Side: Standard Bank Details */}
                <div className="banking-details-box">
                  <div className="bank-meta-header">
                    <HdfcBankIcon />
                    <span>Official Trust Account</span>
                  </div>

                  <div className="banking-info-rows">
  <div className="info-row">
    <span className="lbl">Bank Name:</span>
    <span className="val bold">HDFC Bank</span>
  </div>

  <div className="info-row">
    <span className="lbl">Account Name:</span>
    <span className="val">
      COIMBATORE INSTITUTE OF TECHNOLOGY ALUMNI ASSOCIATION BANGALORE CHAPTER
    </span>
  </div>

  <div className="info-row">
    <span className="lbl">Account No:</span>
    <span className="val highlight">50200118297762</span>
  </div>

  <div className="info-row">
    <span className="lbl">IFSC Code:</span>
    <span className="val bold">HDFC0000075</span>
  </div>
</div>
                </div>

                {/* Right Side: Direct Static Asset Image & Identifier Tag */}
                <div className="banking-qr-visual-side">
                  
                  <div className="qr-asset-card-wrapper">
                    <img 
                      src="/images/payment-qr.png" 
                      alt="Scan to Pay via UPI" 
                      className="qr-static-image-asset"
                      loading="lazy" 
                    />
                  </div>
                  
                 

                </div>

              </div>

            </fieldset>

            {errors.form && <div className="error-banner">{errors.form}</div>}

            <button type="submit" className="submit-btn" disabled={isLoading}>
              {isLoading ? "Creating Account..." : "Complete Registration"}
            </button>

          </form>

        </div>

      </div>

      {/* Success Modal Popup */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="success-popup">
            <div className="success-icon">✓</div>
            <h3>Registration Successful</h3>
            <p>Your alumni account has been created successfully. Redirecting to login.</p>
            <button
              onClick={() => {
                setShowPopup(false);
                navigate("/login");
              }}
              className="popup-close-btn"
            >
              Go to Login
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default AlumniRegistration;